import { TokenManager, authenticatedFetch } from "@/supabase/supabaseClient";
import type { UserProfile } from "@/types/user";
import type { Role } from "@/types/rbac";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export class UserService {
  private static mapUserProfile(raw: Record<string, unknown>): UserProfile {
    const {
      id,
      email,
      first_name,
      last_name,
      created_at,
      updated_at,
      user_roles,
    } = raw as {
      id?: string;
      email?: string;
      first_name?: string;
      last_name?: string;
      created_at?: string;
      updated_at?: string;
      user_roles?: { roles?: Role }[];
    };

    const rbacRoles = Array.isArray(user_roles)
      ? user_roles
          .map((entry) => entry.roles)
          .filter((roleEntry): roleEntry is Role => Boolean(roleEntry))
      : [];

    return {
      id: id ?? "",
      email: email ?? "",
      first_name,
      last_name,
      created_at: created_at ?? new Date().toISOString(),
      updated_at: updated_at ?? new Date().toISOString(),
      rbac_roles: rbacRoles,
    };
  }

  private static buildUserQuery(): string {
    const params = new URLSearchParams({
      select:
        "id,email,first_name,last_name,created_at,updated_at,user_roles!user_roles_user_id_fkey(role_id,roles(id,name,description,is_system,is_active))",
      order: "email",
    });

    // Note: includeAdmins parameter is now deprecated since we're using RBAC
    // All users are fetched and admin status is determined by their rbac_roles

    return `${supabaseUrl}/rest/v1/user_profiles?${params.toString()}`;
  }

  /**
   * Fetch all user profiles
   */
  static async getAllUsers(): Promise<UserProfile[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch users");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(this.buildUserQuery());

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching users:", error);
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const data = await response.json();
      return (data || []).map(this.mapUserProfile);
    } catch (error) {
      console.error("Error in getAllUsers:", error);
      throw error;
    }
  }

  /**
   * Fetch all users including admins
   */
  static async getAllUsersIncludingAdmins(): Promise<UserProfile[]> {
    try {
      // Ensure token is ready before making API call
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch users");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(this.buildUserQuery());

      if (!response.ok) {
        const error = await response.text();
        console.error("❌ Error fetching all users:", error);
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const data = await response.json();

      return (data || []).map(this.mapUserProfile);
    } catch (error) {
      console.error("Error in getAllUsersIncludingAdmins:", error);
      throw error;
    }
  }

  /**
   * Get user profile by ID
   */
  static async getUserById(id: string): Promise<UserProfile | null> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch user by ID");
        throw new Error("Authentication token not available");
      }

      const params = new URLSearchParams({
        select:
          "id,email,first_name,last_name,created_at,updated_at,user_roles!user_roles_user_id_fkey(role_id,roles(id,name,description,is_system,is_active))",
        id: `eq.${id}`,
      });

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/user_profiles?${params.toString()}`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching user by ID:", error);
        return null;
      }

      const data = await response.json();
      return data && data.length > 0 ? this.mapUserProfile(data[0]) : null;
    } catch (error) {
      console.error("Error in getUserById:", error);
      return null;
    }
  }
}
